import React, { useState, useEffect } from 'react';
import { checkForExtension } from 'utils/checkForBugherdExtension';

import styles from './../index.module.css';
import { message } from 'antd';
import { default as WebsitesLogo } from './../assets/website_icon.svg';
import InstallExtension from './InstallExtension';
import { useProjectSitesState } from '../../../../clients/providers/ProjectSites';
import { useOrganizationState } from '../../../../clients/providers/Organization';
import { useDesignAssetsState } from '../../../../clients/providers/DesignAssets';
import ProjectSite from './ProjectSite';
import { post } from 'appJS/utils/fetch';
import {
  SortBy as SortSitesBy,
  SitesSortBy,
  SortDirection as SortSitesDirection
} from './SitesSortBy';
import { SitesFilter } from './SitesFilter';

declare global {
  interface Window {
    bugherd: {
      application: {
        attributes: {
          currentUserId: number;
        };
        currentUser: () => {
          get: (key: string) => any;
          id: number;
        };
      };
    };
    project_board_props: {
      setAcceptingFeedback?: (value: boolean) => void;
      acceptingFeedback?: boolean;
      assetFeedbackProps: {
        project: {
          id: string;
          apiKey: string;
          openTasksCount: number;
          organization: {
            projectSiteLabelsAllowed: boolean;
          };
        };
      };
    };
  }
}

export const WebsitesScreen = () => {
  const projectBoardProps = window?.project_board_props;
  const project = projectBoardProps?.assetFeedbackProps?.project;
  const projectId = project?.id;
  const [loadingCoverUrls, setLoadingCoverUrls] = useState({});

  const {
    projectSites,
    handleGetProjectSites,
    proxyHost
  } = useProjectSitesState();
  const { onSharingClick, isGuest } = useDesignAssetsState();
  const { hasProxyEnabledExperiment } = useOrganizationState();
  const [sitesFilter, setSitesFilter] = useState<string>('');
  const [sortSitesDirection, setSortSitesDirection] = useState<
    SortSitesDirection
  >(SortSitesDirection.DESC);
  const [sortSitesBy, setSortSitesBy] = useState<SortSitesBy>(
    SortSitesBy.UPDATED_AT
  );
  const [hasExtensionInstalled, setExtensionInstalled] = useState<boolean>(
    false
  );

  const container =
    document.getElementById('designAssetsBoard') || document.body;
  const apiKey = project?.apiKey;

  const getCoverImage = async id => {
    try {
      await post(`/projects/${projectId}/generate_cover?site_id=${id}`, {
        headers: { 'Content-Type': 'application/json; charset=utf-8' }
      });
      setLoadingCoverUrls(prevState => ({ ...prevState, [id]: true }));
      message.success(
        'Your cover screenshot is being created, and will appear here shortly'
      );
      setTimeout(() => {
        handleGetProjectSites();
        setLoadingCoverUrls(prevState => ({ ...prevState, [id]: false }));
      }, 6000);
    } catch (e) {
      console.error(e);
      message.error('Something went wrong, please try again.');
    }
  };

  const sortProjectSites = (a, b) => {
    const aTime = new Date(a[sortSitesBy]).getTime();
    const bTime = new Date(b[sortSitesBy]).getTime();

    return sortSitesDirection === SortSitesDirection.ASC
      ? aTime - bTime
      : bTime - aTime;
  };

  const showProjectSite = site => {
    const urlMatch = site.url.toLowerCase().includes(sitesFilter.toLowerCase());

    if (!project?.organization?.projectSiteLabelsAllowed) {
      return (
        urlMatch ||
        site.labels.some(label =>
          label.toLowerCase().includes(sitesFilter.toLowerCase())
        )
      );
    }
    return urlMatch;
  };

  useEffect(() => {
    (async () => {
      try {
        const exists = await checkForExtension();
        if (exists) {
          setExtensionInstalled(exists);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const showInstallBh =
    !hasExtensionInstalled &&
    !hasProxyEnabledExperiment &&
    project.openTasksCount === 0;

  const sortedProjectSites = projectSites.sort(sortProjectSites);

  return (
    <div className={styles.websitesScreen}>
      <div className={styles.websiteWrapper}>
        {showInstallBh && (
          <InstallExtension
            showInstallBh={showInstallBh}
            showJavascriptOption={!isGuest}
          />
        )}
        <div className={styles.titleWrapper}>
          <div className={styles.titleWrapperInner}>
            <WebsitesLogo />
            <h2>Websites</h2>
          </div>
          <div className={styles.titleWrapperInner}>
            {false && (
              <SitesFilter
                sitesFilter={sitesFilter}
                setSitesFilter={setSitesFilter}
                projectSiteLabelsAllowed={
                  project?.organization?.projectSiteLabelsAllowed
                }
              />
            )}
            <SitesSortBy
              sortBy={sortSitesBy}
              setSortBy={setSortSitesBy}
              sortDirection={sortSitesDirection}
              setSortDirection={setSortSitesDirection}
              container={container as HTMLDivElement}
            />
          </div>
        </div>
        <div
          className={styles.websiteCards}
          data-cy="websiteCards"
          id="websiteCards"
        >
          {sortedProjectSites.map((site, index) => (
            <ProjectSite
              key={site.id}
              site={site}
              index={index}
              show={showProjectSite(site)}
              project={project}
              apiKey={apiKey}
              proxyHost={proxyHost}
              container={container}
              showProxyPreview={hasProxyEnabledExperiment}
              loadingCoverUrl={loadingCoverUrls[site.id]}
              getCoverImage={() => getCoverImage(site.id)}
              onSharingClick={() => onSharingClick({ siteId: site.id })}
              projectSiteLabelsAllowed={
                project?.organization?.projectSiteLabelsAllowed
              }
              allLabels={Array.from(
                new Set(projectSites.flatMap(s => s.labels))
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
